import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Globe } from "lucide-react";

/**
 * Country picker dropdown — driven by GET /api/countries.
 *
 * Used on:
 *  - Signup form (initial pick, defaults to JM)
 *  - Profile settings (change later)
 *  - First-sign-in modal for Google OAuth users
 *  - Admin Banks tab (scope banks per country)
 *
 * Renders flag + name. Backed by /api/countries — the seed list is the
 * single source of truth for which countries TalaDraw supports.
 */
export default function CountryPicker({
  value,
  onChange,
  disabled = false,
  size = "md",
  className = "",
  testId = "country-picker",
  includeAllOption = false,
}) {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/countries`)
      .then((res) => {
        if (mounted) setCountries(res.data || []);
      })
      .catch(() => {
        if (mounted) setCountries([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const heightCls = size === "sm" ? "h-9 text-xs" : "h-11 text-sm";

  return (
    <div className={`relative ${className}`}>
      <Globe
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none"
        strokeWidth={1.5}
      />
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading}
        className={`w-full ${heightCls} pl-10 pr-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white appearance-none focus:outline-none focus:border-[#00FF94]/50 disabled:opacity-50`}
        data-testid={testId}
      >
        {!value && !includeAllOption && <option value="">Select country…</option>}
        {includeAllOption && <option value="">All countries</option>}
        {countries.map((c) => (
          <option key={c.code} value={c.code}>
            {c.flag} {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}
